// eslint-disable-next-line @github-ui/github-monorepo/prefer-github-ui-react-router
import {matchPath} from 'react-router'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useRouter} from '@tanstack/react-router'
import {useCallback} from 'react'
import {useIsTanStackRouter} from '../tanstack-router'

export interface PreloadableRoute {
  path: string
  preload(params: Record<string, string>, searchParams?: URLSearchParams): void
}

function usePreloadRouteTanStack(_route?: PreloadableRoute): (url: string) => boolean {
  const router = useRouter()

  return useCallback(
    (url: string) => {
      let pathname: string
      let searchParams: URLSearchParams

      try {
        const parsed = new URL(url, ssrSafeLocation.origin)
        pathname = parsed.pathname
        searchParams = parsed.searchParams
      } catch {
        return false
      }

      void router.preloadRoute({to: pathname, search: Object.fromEntries(searchParams)})
      return true
    },
    [router],
  )
}

function usePreloadRouteDataRouter(route?: PreloadableRoute): (url: string) => boolean {
  return useCallback(
    (url: string) => {
      if (!route) return false

      let pathname: string
      let searchParams: URLSearchParams

      try {
        const parsed = new URL(url, ssrSafeLocation.origin)
        pathname = parsed.pathname
        searchParams = parsed.searchParams
      } catch {
        return false
      }

      const match = matchPath(route.path, pathname)
      if (!match) return false

      const params = (match.params ?? {}) as Record<string, string>
      route.preload(params, searchParams)
      return true
    },
    [route],
  )
}

/**
 * Returns a callback that preloads all queries for a given route by parsing the provided URL.
 *
 * In a TanStack Router context, calls `router.preloadRoute()` to preload lazy route
 * components and trigger TanStack Router loaders.
 *
 * In a Data Router context, the URL is matched against the route's path pattern to extract
 * params and search params, which are forwarded to `route.preload()`.
 *
 * @param route - The route to preload queries for. Must have a `path` and `preload` method.
 *                In data router context, `QueryRoute` from `@github-ui/react-core` satisfies this interface.
 * @returns A function that accepts a URL string and triggers preloading.
 *          Returns `true` if the URL matched the route and preload was initiated, `false` otherwise.
 *
 * @example
 * ```tsx
 * const preload = usePreloadRoute(myRoute)
 *
 * <Link to="/users/42" onMouseEnter={() => preload('/users/42')}>
 *   User 42
 * </Link>
 * ```
 */
export function usePreloadRoute(route?: PreloadableRoute): (url: string) => boolean {
  const isTanStack = useIsTanStackRouter()
  const hook = isTanStack ? usePreloadRouteTanStack : usePreloadRouteDataRouter
  return hook(route)
}
